import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/api";
import useIdleTimeout from "@/hooks/useIdleTimeout";
import {
  ChartPieSlice, Buildings, Receipt, CheckSquare, ShoppingCart,
  FileText, Users, Bell, SignOut, CaretRight, Gear, UserCircle,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// Grouped nav structure. Each group has:
//   - key, label, icon (top-level shown in collapsed sidebar by default)
//   - items: [{ to, label, roles, testid? }]  where `roles: null` = everyone.
// A group hides itself when the user can't see any of its items.
// ---------------------------------------------------------------------------
const NAV_GROUPS = [
  {
    key: "dashboard", label: "Dashboard", icon: ChartPieSlice,
    items: [
      { to: "/dashboard", label: "Overall Dashboard", roles: null },
      { to: "/projects", label: "Project Dashboard", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/pnl", label: "Centre Dashboard", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
    ],
  },
  {
    key: "finance", label: "Finance", icon: Receipt,
    items: [
      { to: "/expenses", label: "Expenses", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator", "centre_manager"] },
      { to: "/assets", label: "Assets", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator", "centre_manager"] },
      { to: "/income", label: "Income", roles: ["admin", "director", "finance", "accounts", "ho_approver", "centre_coordinator", "mis"] },
      { to: "/payments", label: "Payments", roles: ["admin", "director", "finance", "accounts", "centre_coordinator", "centre_manager"] },
      { to: "/budgets", label: "Budgets", roles: ["admin", "director", "ho_approver"] },
    ],
  },
  {
    key: "procurement", label: "Procurement", icon: ShoppingCart,
    items: [
      { to: "/procurement/requests", label: "Purchase Requests", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator", "centre_manager"] },
      { to: "/procurement/quotations", label: "Quotations", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator", "centre_manager"] },
      { to: "/procurement/orders", label: "Purchase Orders", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator", "centre_manager"] },
      { to: "/vendors", label: "Vendors", roles: ["admin", "director", "finance", "accounts", "ho_approver", "centre_coordinator", "centre_manager"] },
    ],
  },
  {
    key: "approvals", label: "Approvals", icon: CheckSquare, showInboxBadge: true,
    items: [
      { to: "/my-queue", label: "My Queue", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/approvals", label: "Approvals", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/matrix", label: "Approval Matrix", roles: ["admin", "director"] },
    ],
  },
  {
    key: "projects_centres", label: "Projects & Centres", icon: Buildings,
    items: [
      { to: "/projects", label: "Projects", roles: ["admin", "director"] },
      { to: "/centres", label: "Centres", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/companies", label: "Companies", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/pnl", label: "Centre P&L", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/profit-share", label: "Coordinator Profit Share", roles: ["admin", "director", "finance", "accounts", "centre_coordinator"] },
    ],
  },
  {
    key: "reports", label: "Reports", icon: FileText,
    items: [
      { to: "/reports", label: "Financial Reports", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/reports?tab=project", label: "Project Reports", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/reports?tab=centre", label: "Centre Reports", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/reports?tab=company", label: "Company P&L", roles: ["admin", "director", "finance", "accounts", "ho_approver", "project_head", "centre_coordinator"] },
      { to: "/reports?tab=payment", label: "Payment Reports", roles: ["admin", "director", "finance", "accounts"] },
      { to: "/reports?tab=profit-share", label: "Profit Share Reports", roles: ["admin", "director", "finance", "accounts"] },
    ],
  },
  {
    key: "administration", label: "Administration", icon: Gear,
    items: [
      { to: "/users", label: "Users", roles: ["admin", "director"] },
      { to: "/emails", label: "Portal Log", roles: ["admin", "director", "accounts"] },
      { to: "/audit", label: "Audit", roles: ["admin", "director", "accounts"] },
      { to: "/file-health", label: "File Health", roles: ["admin", "director"] },
      { to: "/settings", label: "Settings", roles: null },
    ],
  },
];

function itemVisible(item, role) {
  if (!item.roles) return true;
  if (role === "admin") return true;
  return item.roles.includes(role);
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function NavGroup({ group, visibleItems, expanded, onToggle, currentPath, inboxCount }) {
  const Icon = group.icon;
  const isActiveGroup = visibleItems.some((it) =>
    currentPath === it.to.split("?")[0]
  );
  const showBadge = group.showInboxBadge && inboxCount > 0;

  return (
    <div data-testid={`nav-group-${group.key}`}>
      <button
        onClick={onToggle}
        data-testid={`nav-group-toggle-${group.key}`}
        aria-expanded={expanded}
        className={`w-full flex items-center gap-3 px-6 py-2.5 text-sm border-l-2 transition-colors duration-150 ${
          isActiveGroup
            ? "border-white bg-zinc-900 text-white"
            : "border-transparent text-zinc-300 hover:text-white hover:bg-zinc-900"
        }`}
      >
        <Icon size={16} weight="regular" />
        <span className="flex-1 text-left">{group.label}</span>
        {showBadge && (
          <span
            className="min-w-5 h-5 px-1.5 bg-amber-500 text-zinc-950 text-[10px] font-medium flex items-center justify-center rounded-full tabular-nums"
            data-testid={`nav-inbox-badge-${group.key}`}
          >
            {inboxCount > 99 ? "99+" : inboxCount}
          </span>
        )}
        <CaretRight
          size={12}
          className={`transition-transform duration-150 ${expanded ? "rotate-90" : ""}`}
        />
      </button>
      {expanded && (
        <div className="bg-zinc-900/40 border-l-2 border-zinc-800" data-testid={`nav-group-items-${group.key}`}>
          {visibleItems.map((item) => (
            <NavLink
              key={item.to + item.label}
              to={item.to}
              data-testid={`nav-${slug(item.label)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 pl-14 pr-6 py-2 text-[13px] transition-colors duration-150 ${
                  isActive
                    ? "text-white bg-zinc-900"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                }`
              }
            >
              <span className="w-1 h-1 rounded-full bg-current opacity-50" />
              <span className="flex-1">{item.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navi = useNavigate();
  const loc = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [inboxCount, setInboxCount] = useState(0);
  const [openNotif, setOpenNotif] = useState(false);
  const [openUserMenu, setOpenUserMenu] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  useIdleTimeout();

  // Compute filtered groups (hide empty groups where user has no access)
  const visibleGroups = useMemo(() => {
    if (!user) return [];
    return NAV_GROUPS
      .map((g) => ({ ...g, visibleItems: g.items.filter((i) => itemVisible(i, user.role)) }))
      .filter((g) => g.visibleItems.length > 0);
  }, [user]);

  // Auto-expand the group containing the current route
  useEffect(() => {
    const current = loc.pathname;
    const groupToOpen = visibleGroups.find((g) =>
      g.visibleItems.some((it) => it.to.split("?")[0] === current)
    );
    if (groupToOpen) {
      setExpanded((prev) => (prev[groupToOpen.key] ? prev : { ...prev, [groupToOpen.key]: true }));
    }
  }, [loc.pathname, visibleGroups]);

  // Force password change for freshly-invited users
  useEffect(() => {
    if (user?.must_change_password && loc.pathname !== "/settings") {
      navi("/settings?force=1", { replace: true });
    }
  }, [user, loc.pathname, navi]);

  useEffect(() => {
    const loadNotifs = () =>
      api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    const loadInbox = () =>
      api.get("/inbox").then((r) => setInboxCount(r.data.count || 0)).catch(() => {});
    loadNotifs();
    loadInbox();
    const t = setInterval(() => { loadNotifs(); loadInbox(); }, 15000);
    return () => clearInterval(t);
  }, [loc.pathname]);

  const unread = notifs.filter((n) => !n.read).length;

  const markRead = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch {}
    setNotifs((cur) => cur.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
  };
  const markAllRead = async () => {
    const unreadIds = notifs.filter((n) => !n.read).map((n) => n.id);
    await Promise.all(unreadIds.map((id) => api.post(`/notifications/${id}/read`).catch(() => {})));
    setNotifs((cur) => cur.map((x) => ({ ...x, read: true })));
  };
  const openNotification = async (n) => {
    setOpenNotif(false);
    if (!n.read) markRead(n);
    if (n.link) navi(n.link);
  };
  const doLogout = async () => { await logout(); navi("/login"); };

  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="min-h-screen flex bg-zinc-50 text-zinc-900">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      {/* Sidebar */}
      <aside
        className={`w-64 shrink-0 bg-zinc-950 text-zinc-100 flex flex-col border-r border-zinc-900 fixed lg:static inset-y-0 left-0 z-40 transform transition-transform duration-200 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="app-sidebar"
      >
        <div className="px-6 py-6 border-b border-zinc-800 flex items-center justify-center">
          <img src="/param-logo.png" alt="Param Skills Training (I) Pvt. Ltd."
            className="h-20 w-auto object-contain brightness-0 invert"
            data-testid="sidebar-logo" />
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {visibleGroups.map((group) => (
            <NavGroup
              key={group.key}
              group={group}
              visibleItems={group.visibleItems}
              expanded={!!expanded[group.key]}
              onToggle={() => toggle(group.key)}
              currentPath={loc.pathname}
              inboxCount={inboxCount}
            />
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-800 text-xs">
          <div className="text-zinc-400 truncate">{user?.name}</div>
          <div className="text-zinc-500 text-[10px] uppercase tracking-widest">{ROLES[user?.role]}</div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="mt-3 flex items-center gap-2 text-zinc-300 hover:text-white text-xs"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4 lg:px-8 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 hover:bg-zinc-100 rounded-sm"
              data-testid="sidebar-toggle"
              aria-label="Toggle sidebar"
            >
              <span className="block w-5 h-0.5 bg-zinc-800 mb-1"></span>
              <span className="block w-5 h-0.5 bg-zinc-800 mb-1"></span>
              <span className="block w-5 h-0.5 bg-zinc-800"></span>
            </button>
            <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 hidden sm:block">
              Project-Wise Financial Operations
            </div>
          </div>
          <div className="flex items-center gap-2 relative">
            <button
              onClick={() => { setOpenNotif(!openNotif); setOpenUserMenu(false); }}
              data-testid="notification-bell"
              className="relative p-2 hover:bg-zinc-100 rounded-sm"
            >
              <Bell size={18} />
              {(unread + inboxCount) > 0 && (
                <span
                  data-testid="bell-badge"
                  className="absolute top-1 right-1 h-4 min-w-4 px-1 bg-rose-600 text-white text-[10px] flex items-center justify-center rounded-full tabular-nums"
                >
                  {(unread + inboxCount) > 99 ? "99+" : (unread + inboxCount)}
                </span>
              )}
            </button>
            {openNotif && (
              <div className="absolute right-0 top-12 w-96 bg-white border border-zinc-200 shadow-lg z-20" data-testid="notifications-panel">
                <div className="p-3 border-b border-zinc-200 flex justify-between items-center">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Notifications</div>
                  {unread > 0 && (
                    <button
                      onClick={markAllRead}
                      data-testid="mark-all-read"
                      className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-black"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                {inboxCount > 0 && (
                  <button
                    data-testid="bell-queue-preview"
                    onClick={() => { setOpenNotif(false); navi("/my-queue"); }}
                    className="w-full text-left p-3 border-b border-zinc-200 bg-amber-50 hover:bg-amber-100 flex justify-between items-center"
                  >
                    <div>
                      <div className="text-xs uppercase tracking-widest text-amber-800">Awaiting your action</div>
                      <div className="text-sm font-medium text-zinc-900 mt-0.5">
                        {inboxCount} item{inboxCount === 1 ? "" : "s"} in My Queue
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Open →</div>
                  </button>
                )}
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 && inboxCount === 0 && (
                    <div className="p-8 text-center text-sm text-zinc-500">You're all caught up.</div>
                  )}
                  {notifs.length === 0 && inboxCount > 0 && (
                    <div className="p-4 text-xs text-zinc-500">No message notifications — see queue above.</div>
                  )}
                  {notifs.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => openNotification(n)}
                      data-testid={`notif-item-${n.id}`}
                      className={`w-full text-left p-3 border-b border-zinc-100 hover:bg-zinc-50 transition ${
                        !n.read ? "bg-white" : "opacity-60"
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-rose-600 shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{n.title}</div>
                          <div className="text-xs text-zinc-500 truncate">{n.body}</div>
                          <div className="text-[10px] text-zinc-400 mt-1">{new Date(n.at).toLocaleString()}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* User menu — name + settings + sign out on the top-right */}
            <button
              onClick={() => { setOpenUserMenu(!openUserMenu); setOpenNotif(false); }}
              data-testid="user-menu-btn"
              className="flex items-center gap-2 p-1.5 pr-2.5 hover:bg-zinc-100 rounded-sm"
              aria-label="Account menu"
            >
              <UserCircle size={22} weight="duotone" className="text-zinc-700" />
              <span className="hidden sm:block text-left leading-tight">
                <span className="block text-xs font-medium text-zinc-900 max-w-[140px] truncate">{user?.name}</span>
                <span className="block text-[9px] uppercase tracking-widest text-zinc-500">{ROLES[user?.role] || user?.role}</span>
              </span>
              <CaretRight
                size={10}
                className={`transition-transform duration-150 text-zinc-500 hidden sm:block ${openUserMenu ? "rotate-90" : ""}`}
              />
            </button>
            {openUserMenu && (
              <div
                className="absolute right-0 top-12 w-64 bg-white border border-zinc-200 shadow-lg z-20"
                data-testid="user-menu-panel"
              >
                <div className="p-3 border-b border-zinc-100">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">{ROLES[user?.role] || user?.role}</div>
                </div>
                <button
                  onClick={() => { setOpenUserMenu(false); navi("/settings"); }}
                  data-testid="user-menu-settings"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 flex items-center gap-2"
                >
                  <Gear size={14} className="text-zinc-500" /> Account & Settings
                </button>
                <button
                  onClick={() => { setOpenUserMenu(false); doLogout(); }}
                  data-testid="user-menu-logout"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-rose-50 text-rose-700 flex items-center gap-2 border-t border-zinc-100"
                >
                  <SignOut size={14} /> Sign out
                </button>
              </div>
            )}
          </div>
        </header>
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
